import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import { useTheme, useMediaQuery } from "@mui/material";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import ContainerPage from "../../components/Containers/ContainerPage";
import CampaignApprovalLoader from "../../components/CampaignApproval/CampaignApprovalLoader";
import LanguageSwitcher from "../../components/Common/LanguageSwitcher";
import {
  startDecideCampaignApproval,
  startGetCampaignApprovalPayload,
  startSubmitCampaignApprovalComment,
} from "../../actions/campaignApproval";
import ApprovalSidebar from "./components/ApprovalSidebar";
import AdGroupSection from "./components/AdGroupSection";
import DecideBar from "./components/DecideBar";
import {
  CAMPAIGN_APPROVAL_CONTENT_EMPTY,
  CAMPAIGN_APPROVAL_EXPIRED_DESCRIPTION,
  CAMPAIGN_APPROVAL_EXPIRED_TITLE,
  CAMPAIGN_APPROVAL_INVALID_DESCRIPTION,
  CAMPAIGN_APPROVAL_INVALID_TITLE,
  CAMPAIGN_APPROVAL_LOCKED_STATUS_APPROVED,
  CAMPAIGN_APPROVAL_PAGE_TITLE,
  READY,
  TOKEN_EXPIRED,
  TOKEN_INVALID,
} from "../../i18n/keysTranslations";
import { ALERT_ICON_TYPE_SUCCESS } from "../../utils/constants";
import {
  normalizeAppLanguage,
  persistAppLanguage,
} from "../../utils/i18nLanguage";
import { SimpleAlert } from "../../components/Alerts/Alerts";
import AdAccordion from "./components/AdAccordion";
import {
  groupApprovalEntitiesForDisplay,
  campaignApprovalMainContentSx,
  campaignApprovalMainPaddingSx,
  campaignApprovalNoticeAlertSx,
} from "./utils";
import {
  getCampaignReviewerPermissions,
  getMyReviewerStatusForCampaign,
  mergeReviewerStatusIntoApproval,
} from "../../components/ContentApproval/approvalReviewersUtils";

const LANGUAGE_SWITCHER_STYLES = {
  position: "fixed",
  right: 24,
  zIndex: 1300,
};

const LANGUAGE_SWITCHER_BUTTON_STYLES = {
  minWidth: "max-content",
  border: "none !important",
  boxShadow: "none !important",
  backgroundColor: "transparent !important",
  px: "0 !important",
  "&.MuiButtonBase-root.MuiButton-root:hover": {
    backgroundColor: "transparent !important",
    border: "none !important",
    boxShadow: "none !important",
    color: "inherit !important",
  },
};

const LANGUAGE_SWITCHER_MENU_STYLES = {
  "& .MuiPaper-root": {
    marginTop: "8px",
  },
};

const CampaignApproval = () => {
  const dispatch = useDispatchApp();
  const { t, i18n } = useTranslationApp();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const appTheme = useSelectorApp((state) => state.ui.theme);
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [payload, setPayload] = useState(null);
  const [approval, setApproval] = useState(null);
  const [comments, setComments] = useState([]);
  const [accessErrorCode, setAccessErrorCode] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [campaignID, setCampaignID] = useState("");
  const [currentReviewer, setCurrentReviewer] = useState(null);

  const token = searchParams.get("token") || "";
  const platforms = useMemo(() => payload?.platforms || [], [payload]);
  const currentLanguage = normalizeAppLanguage(i18n.language) || "en";

  const onChangeLanguage = (language) => {
    const nextLanguage = normalizeAppLanguage(language);
    if (!nextLanguage || nextLanguage === currentLanguage) {
      return;
    }
    i18n.changeLanguage(nextLanguage);
    persistAppLanguage(nextLanguage);
  };

  const loadCampaignApprovalPayload = useCallback(async () => {
    if (!token) {
      setAccessErrorCode(TOKEN_INVALID);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const response = await dispatch(startGetCampaignApprovalPayload(token));

    if (!response?.ok) {
      setAccessErrorCode(response?.code || TOKEN_INVALID);
      setPayload(null);
      setApproval(null);
      setComments([]);
      setCampaignID("");
      setCurrentReviewer(null);
      setSelectedPlatform("");
      setIsLoading(false);
      return;
    }

    setPayload(response.payload || null);
    setApproval(response.approval || null);
    setComments(response.comments || []);
    setCampaignID(response.campaignID || "");
    setCurrentReviewer(response.currentReviewer || null);
    setAccessErrorCode("");
    setIsLoading(false);
  }, [dispatch, token]);

  useEffect(() => {
    loadCampaignApprovalPayload();
  }, [loadCampaignApprovalPayload]);

  useEffect(() => {
    if (!platforms.length) {
      setSelectedPlatform("");
      return;
    }

    setSelectedPlatform((currentPlatform) => {
      if (
        currentPlatform &&
        platforms.some(
          (platformData) => platformData.platform === currentPlatform
        )
      ) {
        return currentPlatform;
      }

      return platforms[0]?.platform || "";
    });
  }, [platforms]);

  const selectedPlatformData = useMemo(() => {
    return (
      platforms.find(
        (platformData) => platformData.platform === selectedPlatform
      ) || null
    );
  }, [platforms, selectedPlatform]);

  const approvalSections = useMemo(() => {
    return groupApprovalEntitiesForDisplay(
      selectedPlatformData?.entities || []
    );
  }, [selectedPlatformData]);

  const { commentsLocked, canApprove, myReviewerStatus } = useMemo(
    () =>
      getCampaignReviewerPermissions({
        approval,
        currentReviewer,
      }),
    [approval, currentReviewer]
  );

  const lockedMessageKey = useMemo(() => {
    if (!approval) {
      return "";
    }

    if (myReviewerStatus === "approved" || approval.status === "approved") {
      return CAMPAIGN_APPROVAL_LOCKED_STATUS_APPROVED;
    }

    return "";
  }, [approval, myReviewerStatus]);

  const onHandleComment = async ({ body, target = null }) => {
    setIsSubmittingComment(true);
    const response = await dispatch(
      startSubmitCampaignApprovalComment({
        token,
        type: target ? "specific" : "general",
        body,
        target,
      })
    );
    setIsSubmittingComment(false);

    if (response?.ok && response?.comment) {
      setComments((currentComments) => [...currentComments, response.comment]);
      setApproval((currentApproval) => {
        if (!currentApproval) {
          return currentApproval;
        }

        const nextStatus = response.status || "rejected";
        let nextApproval = {
          ...currentApproval,
          status: nextStatus,
        };

        if (currentReviewer?.reviewerID) {
          const reviewerStatus = getMyReviewerStatusForCampaign({
            approval: currentApproval,
            currentReviewerID: currentReviewer.reviewerID,
          });

          if (reviewerStatus !== "approved") {
            nextApproval = mergeReviewerStatusIntoApproval({
              approval: nextApproval,
              reviewerID: currentReviewer.reviewerID,
              status: "rejected",
            });
          }
        }

        return nextApproval;
      });
      await SimpleAlert({
        title: t(READY),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
    }

    if (!response?.ok && response?.code === "approvalClosed") {
      setApproval((currentApproval) =>
        currentApproval
          ? {
              ...currentApproval,
              hasDecisionForCurrentDraft: true,
            }
          : currentApproval
      );
    }

    return response;
  };

  const onHandleDecision = async ({ decision, body }) => {
    const response = await dispatch(
      startDecideCampaignApproval({
        token,
        decision,
        body,
      })
    );

    if (response?.ok) {
      setApproval((currentApproval) => {
        if (!currentApproval) {
          return currentApproval;
        }

        const nextStatus = response.status || decision;
        let nextApproval = {
          ...currentApproval,
          status: nextStatus,
        };

        if (currentReviewer?.reviewerID) {
          nextApproval = mergeReviewerStatusIntoApproval({
            approval: nextApproval,
            reviewerID: currentReviewer.reviewerID,
            status: "approved",
          });
        }

        const myReviewerStatus = getMyReviewerStatusForCampaign({
          approval: nextApproval,
          currentReviewerID: currentReviewer?.reviewerID || "",
        });

        if (myReviewerStatus === "approved" || nextStatus === "approved") {
          nextApproval = {
            ...nextApproval,
            hasDecisionForCurrentDraft: true,
          };
        }

        return nextApproval;
      });

      if (response?.comment) {
        setComments((currentComments) => [
          ...currentComments,
          response.comment,
        ]);
      }
    }

    return response;
  };

  const onSelectPlatform = (platform) => {
    setSelectedPlatform(platform);
    if (isTablet) {
      setMobileSidebarOpen(false);
    }
  };

  const renderErrorState = () => {
    const isExpired = accessErrorCode === TOKEN_EXPIRED;
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: 760,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          p: { xs: 3, md: 4 },
          bgcolor: "background.paper",
          m: "auto",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {t(
            isExpired
              ? CAMPAIGN_APPROVAL_EXPIRED_TITLE
              : CAMPAIGN_APPROVAL_INVALID_TITLE
          )}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t(
            isExpired
              ? CAMPAIGN_APPROVAL_EXPIRED_DESCRIPTION
              : CAMPAIGN_APPROVAL_INVALID_DESCRIPTION
          )}
        </Typography>
      </Box>
    );
  };

  return (
    <ContainerPage
      hasMarginBottom={false}
      sx={{
        width: "100%",
        height: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
        p: 0,
        pb: 0,
        px: "8px !important",
        alignItems: "stretch",
      }}
    >
      <Helmet>
        <title>{`${appTheme?.nameBusiness} - ${t(CAMPAIGN_APPROVAL_PAGE_TITLE)}`}</title>
      </Helmet>

      <Box sx={LANGUAGE_SWITCHER_STYLES}>
        <LanguageSwitcher
          language={currentLanguage}
          onChangeLanguage={onChangeLanguage}
          colorButton="secondary"
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={LANGUAGE_SWITCHER_BUTTON_STYLES}
          sxMenu={LANGUAGE_SWITCHER_MENU_STYLES}
        />
      </Box>

      {isLoading ? (
        <CampaignApprovalLoader open />
      ) : accessErrorCode ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
          }}
        >
          {renderErrorState()}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "100%",
            minHeight: 0,
            bgcolor: "background.default",
          }}
        >
          <ApprovalSidebar
            platforms={platforms}
            selectedPlatform={selectedPlatform}
            onSelectPlatform={onSelectPlatform}
            clientName={payload?.clientName || ""}
            mobileOpen={mobileSidebarOpen}
            onMobileClose={() => setMobileSidebarOpen(false)}
            currentReviewer={currentReviewer}
            reviewers={approval?.reviewers || []}
          />

          <Box
            component="main"
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {isTablet ? (
              <Box
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 1.5,
                }}
              >
                <IconButton
                  aria-label="open campaigns"
                  onClick={() => setMobileSidebarOpen(true)}
                >
                  <MenuIcon />
                </IconButton>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {selectedPlatformData?.campaignName ||
                    payload?.campaignName ||
                    t(CAMPAIGN_APPROVAL_PAGE_TITLE)}
                </Typography>
              </Box>
            ) : null}

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
              }}
            >
              <Box
                sx={{
                  ...campaignApprovalMainPaddingSx,
                  pt: { xs: 2, md: 3 },
                }}
              >
                <Box sx={campaignApprovalMainContentSx}>
                  {lockedMessageKey ? (
                    <Alert
                      severity="info"
                      sx={{ ...campaignApprovalNoticeAlertSx, mb: 3 }}
                    >
                      {t(lockedMessageKey)}
                    </Alert>
                  ) : null}

                  {platforms.length === 0 ? (
                    <Alert severity="info">
                      {t(CAMPAIGN_APPROVAL_CONTENT_EMPTY)}
                    </Alert>
                  ) : (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                    >
                      {approvalSections.flatMap((section) => {
                        const sectionProps = {
                          token,
                          campaignID,
                          comments,
                          currentDraftID: approval?.currentDraftID || "",
                          commentsLocked,
                          isSubmittingComment,
                          onSubmitSpecificComment: ({ body, target }) =>
                            onHandleComment({ body, target }),
                        };

                        if (section.type === "assetsGroup") {
                          return section.entities.map((entity) => (
                            <AdAccordion
                              key={`${section.key}-${entity.entityID}`}
                              entity={entity}
                              {...sectionProps}
                            />
                          ));
                        }

                        return (
                          <AdGroupSection
                            key={section.key}
                            adGroupName={section.title}
                            entities={section.entities}
                            {...sectionProps}
                          />
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <DecideBar
              disabled={commentsLocked}
              isApproveDisabled={!canApprove}
              comments={comments}
              currentDraftID={approval?.currentDraftID || ""}
              onSubmitDecision={onHandleDecision}
            />
          </Box>
        </Box>
      )}
    </ContainerPage>
  );
};

export default CampaignApproval;
